import React from "react";
import type { PayCashierMethodOption, PayCashierSessionView } from "./CashierType";
import { formatAmountYuan } from "./utils/device";
import {
  AlipayIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  WechatPayIcon,
} from "./CashierIcons";
import { CashierBrandLogo } from "./CashierBrandLogo";
import { CashierPayBlockedPanel } from "./CashierPayBlockedPanel";
import type { CashierExpiredPrompt } from "./cashierExpiredError";
import { formatCashierOrderTime } from "./cashierDateTime";
import {
  isCashierMethodClickableWhenDisabled,
  resolveEnterpriseMethodTag,
} from "./cashierEnterpriseMethodDisplay";
import "./CashierStyle.css";

export interface CashierViewProps {
  session: PayCashierSessionView;
  paying: boolean;
  paid: boolean;
  payBlocked?: CashierExpiredPrompt | null;
  wechatNativeCodeUrl?: string;
  onSelectMethod: (method: PayCashierMethodOption) => void;
  onBack?: () => void;
}

const WechatIcon: React.FC = () => (
  <WechatPayIcon className="cashier-pay-method-icon" size={28} />
);

const AlipayIconSmall: React.FC = () => (
  <AlipayIcon className="cashier-pay-method-icon" size={28} />
);

export const CashierH5View: React.FC<CashierViewProps> = ({
  session,
  paying,
  paid,
  payBlocked,
  onSelectMethod,
  onBack,
}) => {
  const methods = session.payMethods ?? [];

  return (
    <div className="cashier-shell cashier-shell-h5">
      <header className="cashier-h5-header">
        {onBack ? (
          <button type="button" className="cashier-back-btn" onClick={onBack}>
            返回
          </button>
        ) : (
          <span />
        )}
        <span className="cashier-h5-title">
          <CashierBrandLogo className="cashier-brand-logo" size={22} />
          收银台
        </span>
        <span className="cashier-h5-spacer" />
      </header>

      <section className="cashier-h5-amount-block">
        <p className="cashier-h5-amount-label">支付金额</p>
        <p className="cashier-h5-amount">
          <span className="cashier-currency">¥</span>
          {formatAmountYuan(session.payAmountYuan)}
        </p>
        <p className="cashier-h5-subject">{session.subject}</p>
        {session.productSummary ? (
          <p className="cashier-h5-summary">{session.productSummary}</p>
        ) : null}
      </section>

      {paid ? (
        <div className="cashier-paid-banner cashier-paid-banner-h5">
          <CheckCircleIcon className="cashier-paid-icon" size={24} />
          <span>支付成功</span>
        </div>
      ) : payBlocked ? (
        <CashierPayBlockedPanel prompt={payBlocked} onBack={onBack} />
      ) : (
        <section className="cashier-h5-methods">
          <p className="cashier-section-title">选择支付方式</p>
          <ul className="cashier-method-list cashier-method-list-h5">
            {methods.map((m) => {
              const enterpriseTag = resolveEnterpriseMethodTag(m);
              const visuallyDisabled = !m.enabled;
              const buttonDisabled =
                paying ||
                paid ||
                Boolean(payBlocked) ||
                (visuallyDisabled && !isCashierMethodClickableWhenDisabled(m));
              return (
                <li key={m.code}>
                  <button
                    type="button"
                    className={
                      visuallyDisabled
                        ? "cashier-method-item cashier-method-item-disabled"
                        : "cashier-method-item"
                    }
                    disabled={buttonDisabled}
                    onClick={() => onSelectMethod(m)}
                  >
                    {m.code === "wechat" ? <WechatIcon /> : <AlipayIconSmall />}
                    <span className="cashier-method-label">{m.label}</span>
                    {enterpriseTag ? (
                      <span className="cashier-method-tag">{enterpriseTag}</span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
          {paying ? <p className="cashier-paying-hint">正在跳转支付…</p> : null}
        </section>
      )}

      <footer className="cashier-h5-footer">
        <div className="cashier-trust-row cashier-trust-row-h5">
          <span className="cashier-trust-badge">
            <ShieldCheckIcon size={12} />
            安全加密
          </span>
          <span className="cashier-trust-badge">
            <CheckCircleIcon size={12} />
            官方通道
          </span>
        </div>
        <span>订单号 {session.bizOrderId}</span>
        {session.expireAt ? (
          <span>有效期至 {formatCashierOrderTime(session.expireAt)}</span>
        ) : null}
      </footer>
    </div>
  );
};
